import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  GuardResult,
  MaybeAsync,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { map, take } from 'rxjs';
import { RoutePath } from '../enums/routes.enum';
import {
  AuthGuardData,
  FirebaseAuthService,
} from '../services/firebase-auth.service';

export const authGuard: CanActivateFn = (
  _: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): MaybeAsync<GuardResult> => {
  const firebaseAuthService = inject(FirebaseAuthService);
  const router = inject(Router);

  return firebaseAuthService.authGuardData$.pipe(
    take(1),
    map((authGuardData: AuthGuardData) => {
      const { emailVerified, token } = authGuardData;

      // Pages accessible without authentication
      if (
        state.url.startsWith(`/${RoutePath.Landing}`) ||
        state.url.startsWith(`/${RoutePath.ResetPassword}`)
      ) {
        return token == null
          ? true
          : router.createUrlTree([RoutePath.Dashboard]);
      }

      // Public versions of pages accessible without authentication
      // When authenticated, redirect to the proper page
      if (state.url.startsWith(`/${RoutePath.Public}`)) {
        if (token == null) {
          return true;
        }
        return router.createUrlTree(
          /**
           * Remove query params if any, split to sections, remove "" and "public", navigate to path
           * example url => /public/marketplace/bfd9f2b9-e1b1-4287-8297-48ed4b48fb89?__se_s=696c9aa7d5754dc2b576b0356c782739&__se_t=n"
           * result for "example url" => ['marketplace', 'bfd9f2b9-e1b1-4287-8297-48ed4b48fb89']
           */
          (state.url.split('?')[0] || '').split('/').slice(2),
          { queryParams: { ...state.root.queryParams } },
        );
      }

      // If not authenticated, redirect to the landing page
      if (token == null) {
        firebaseAuthService.setRedirectUrl(state.url);
        return router.createUrlTree([RoutePath.Landing]);
      }

      // Auth actions
      if (state.url.startsWith(`/${RoutePath.AuthAction}`)) {
        return true;
      }

      // Others (authenticated + email verified)
      return emailVerified
        ? true
        : router.createUrlTree([RoutePath.AuthAction]);
    }),
  );
};
